import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputLabel,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";
import { initiateAXIOS } from "../../config/axios";
import { API_LIMIT, initiateOrganizationAPI } from "../../api";
import { createViewer } from "../../store/actions/viewerAction";
import Role from "../../helpers/roles";
import { logger } from "../../helpers/logger";
import { TypographySmall } from "../customComponent";
import { colorStyling } from "../../helpers/color";
import DispatchErrorPromptAlert from "../DispatchErrorPromptAlert";
import { SET_ERROR_PROMPT_VIEWER_DIALOG } from "../../store/actionTypes/viewerActionTypes";
import { parseVariables } from "../../helpers/localize";
import { sanitizeInput } from "../../helpers/inputProcessing";
import { isEmpty } from "lodash";
import { stellarFlag } from "../../externalFeatures/componentsFlag";

const StyledBox = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  ":last-child": {
    marginBottom: theme.spacing(0),
  },
}));

const CreateViewer = () => {
  const dispatch = useDispatch();
  const {
    isErrorPromptViewerDialog: isError,
    errorMessageViewerDialog: errorMessage,
  } = useSelector((state) => state.viewer);

  const { t } = useTranslation("viewer");
  const { t: tCommon } = useTranslation();

  const { user } = useSelector((state) => state.auth);
  const userRole = user?.type;
  const userOrg = user?.organization?.id;

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [viewerName, setViewerName] = useState("");
  const [viewerKey, setViewerKey] = useState("");
  const [viewerLocation, setViewerLocation] = useState("");
  const [viewerLocationDesc, setViewerLocationDesc] = useState("");
  const [organizations, setOrganizations] = useState([]);
  const [selectOrganization, setSelectOrganization] = useState("");
  const [nameValidation, setNameValidation] = useState("");
  const [keyValidation, setKeyValidation] = useState("");
  const [coordinate, setCordinate] = useState(false);
  const [latitude, setLatitude] = useState(0);
  const [longitude, setLongitude] = useState(0);
  const maxLocationLength = 60;

  const [vistarDeviceID, setVistarDeviceID] = useState("");
  const [vistarVenueID, setVistarVenueID] = useState("");
  const [hivestackID, setHivestackID] = useState("");

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClickClose = () => {
    setViewerName("");
    setViewerKey("");
    setOrganizations([]);
    setSelectOrganization("");
    setNameValidation("");
    setKeyValidation("");
    setVistarDeviceID("");
    setVistarVenueID("");
    setHivestackID("");
    dispatch({ type: SET_ERROR_PROMPT_VIEWER_DIALOG, payload: false });
    setOpen(false);
  };

  const handleInputName = (input) => {
    if (input.length > 40)
      setNameValidation(
        parseVariables(tCommon("validation.maxLengthChars"), {
          num: 40,
        })
      );
    else {
      setViewerName(input);
      if (nameValidation) setNameValidation("");
    }
  };

  const handleInputLocation = (input) => {
    if (input.length <= maxLocationLength) setViewerLocation(input);
  };
  const handleInputLocationDesc = (input) => {
    if (input.length <= maxLocationLength) setViewerLocationDesc(input);
  };

  const handleInputKey = (input) => {
    if (input.length > 6) setKeyValidation(t("validation.keyMaxLength"));
    else {
      setViewerKey(input);
      if (keyValidation) setKeyValidation("");
    }
  };

  const handleCoordinateToggle = (e) => {
    setCordinate(e.target.checked);
  };

  const handleCoordinateChange = (line, e) => {
    let val = e.target.value;

    val = val.replace(/^(-?)0+(\d)/, "$1$2");
    const num = parseFloat(val);

    if (line === "latitude" && !isNaN(num) && num >= -90 && num <= 90)
      setLatitude(num);
    else if (val === "" || val === "-") {
      setLatitude("0");
    }

    if (line === "longitude" && !isNaN(num) && num >= -180 && num <= 180)
      setLongitude(num);
    else if (val === "" || val === "-") {
      setLongitude("0");
    }
  };

  const handleFinishSubmit = (stateSubmit) => {
    setLoading(false);
    if (stateSubmit === "success") {
      handleClickClose();
    }
  };

  const submitForm = () => {
    if (viewerName.trim() === "") {
      setViewerName("");
      setNameValidation(t("validation.nameEmpty"));
      return;
    }

    if (viewerKey.trim() === "") {
      setViewerKey("");
      setKeyValidation(t("validation.keyEmpty"));
      return;
    }

    const payload = {
      name: viewerName.trim(),
      key: viewerKey.trim(),
      organizationId:
        selectOrganization === "" ? +userOrg : +selectOrganization,
    };

    if (!isEmpty(viewerLocation)) {
      if (isEmpty(payload["metadata"])) payload["metadata"] = {};
      payload["metadata"]["address1"] =
        viewerLocation === "" ? null : sanitizeInput(viewerLocation.trim());
    }

    if (!isEmpty(viewerLocationDesc) && !isEmpty(viewerLocation)) {
      if (isEmpty(payload["metadata"])) payload["metadata"] = {};
      payload["metadata"]["address2"] =
        viewerLocationDesc === ""
          ? null
          : sanitizeInput(viewerLocationDesc.trim());
    }

    if (coordinate) {
      payload["latitude"] = +latitude;
      payload["longitude"] = +longitude;
    }

    if (!isEmpty(vistarDeviceID)) {
      if (isEmpty(payload["metadata"])) payload["metadata"] = {};
      payload["metadata"]["vistarDeviceId"] =
        vistarDeviceID === "" ? null : sanitizeInput(vistarDeviceID.trim());
    }

    if (!isEmpty(vistarVenueID)) {
      if (isEmpty(payload["metadata"])) payload["metadata"] = {};
      payload["metadata"]["vistarVenueId"] =
        vistarVenueID === "" ? null : sanitizeInput(vistarVenueID.trim());
    }

    if (!isEmpty(hivestackID)) {
      if (isEmpty(payload["metadata"])) payload["metadata"] = {};
      payload["metadata"]["hivestackScreenId"] =
        hivestackID === "" ? null : sanitizeInput(hivestackID.trim());
    }

    setLoading(true);
    payload["name"] = sanitizeInput(payload.name);
    dispatch(createViewer(payload, handleFinishSubmit));
  };

  const getOrganizations = async () => {
    const access_token = sessionStorage.getItem("access_token");
    try {
      const { data } = await initiateAXIOS.get(
        initiateOrganizationAPI + `?limit=${API_LIMIT}`,
        {
          headers: { authorization: `Bearer ${access_token}` },
        }
      );

      const listOrganization = data.items;

      let organizationData = [];
      listOrganization.map((item) => organizationData.push(item));
      setOrganizations(organizationData);
    } catch (error) {
      logger.log(error);
    }
  };

  useEffect(() => {
    if (open && userRole === Role.Eins) {
      getOrganizations();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <>
      {userRole && (
        <Button
          variant="contained"
          onClick={handleClickOpen}
          startIcon={<AddIcon fontSize="small" />}
          sx={{ boxShadow: 3, ml: 2 }}
        >
          {t("createBtn")}
        </Button>
      )}

      <Dialog maxWidth="sm" fullWidth open={open} onClose={handleClickClose}>
        <Box>
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {t("form.newDialogTitle")}
            <IconButton onClick={handleClickClose}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <Divider />

          <DialogContent>
            <DispatchErrorPromptAlert
              isError={isError}
              errorMessage={errorMessage}
              closeActionType={SET_ERROR_PROMPT_VIEWER_DIALOG}
            />
            <StyledBox sx={{ mt: 1 }}>
              <InputLabel>{t("form.nameLabel")}</InputLabel>

              <TextField
                autoFocus
                margin="dense"
                size="small"
                fullWidth
                name="viewerName"
                placeholder={t("form.namePlaceholder")}
                value={viewerName}
                onChange={(e) => {
                  handleInputName(e.target.value);
                }}
                disabled={loading}
              />
              <TypographySmall sx={{ color: colorStyling.red }}>
                {nameValidation}
              </TypographySmall>
            </StyledBox>

            <StyledBox sx={{ mt: 1 }}>
              <InputLabel>{t("form.keyLabel")}</InputLabel>

              <TextField
                autoFocus
                size="small"
                margin="dense"
                fullWidth
                name="viewerKey"
                placeholder={t("form.keyPlaceholder")}
                value={viewerKey}
                onChange={(e) => {
                  handleInputKey(e.target.value);
                }}
                disabled={loading}
              />
              <TypographySmall sx={{ color: colorStyling.red }}>
                {keyValidation}
              </TypographySmall>
            </StyledBox>

            <StyledBox sx={{ mt: 1 }}>
              <InputLabel>{t("form.locationLabel")}</InputLabel>

              <TextField
                autoFocus
                size="small"
                margin="dense"
                fullWidth
                name="viewerLocation"
                placeholder={t("form.locationPlaceholder")}
                value={viewerLocation}
                onChange={(e) => {
                  handleInputLocation(e.target.value);
                }}
                disabled={loading}
              />

              {viewerLocation.length >= maxLocationLength && (
                <TypographySmall sx={{ color: colorStyling.red }}>
                  {parseVariables(tCommon("validation.maxLengthChars"), {
                    num: maxLocationLength,
                  })}
                </TypographySmall>
              )}
            </StyledBox>

            {viewerLocation && (
              <StyledBox sx={{ mt: 1 }}>
                <InputLabel>{t("form.locationDescLabel")}</InputLabel>

                <TextField
                  margin="dense"
                  size="small"
                  fullWidth
                  name="viewerLocationDesc"
                  placeholder={t("form.locationDescPlaceholder")}
                  value={viewerLocationDesc}
                  onChange={(e) => {
                    handleInputLocationDesc(e.target.value);
                  }}
                  disabled={loading}
                />

                {viewerLocation.length >= maxLocationLength && (
                  <TypographySmall sx={{ color: colorStyling.red }}>
                    {parseVariables(tCommon("validation.maxLengthChars"), {
                      num: maxLocationLength,
                    })}
                  </TypographySmall>
                )}
              </StyledBox>
            )}

            <Box sx={{ mt: 1 }}>
              <FormControlLabel
                control={<Checkbox />}
                value={coordinate}
                checked={coordinate}
                onChange={handleCoordinateToggle}
                label={t("form.insertCoordinateLabel")}
                disabled={loading}
                sx={{ mb: 1 }}
              />
              {coordinate && (
                <Box sx={{ display: "flex", alignItems: "end", mb: 1 }}>
                  <Box sx={{ width: "100%" }}>
                    <TextField
                      fullWidth
                      label={t("form.latitudeLabel")}
                      placeholder={t("form.coordinatePlaceholder")}
                      type="text"
                      value={latitude}
                      onChange={(e) => handleCoordinateChange("latitude", e)}
                      size="small"
                      disabled={loading}
                    />
                  </Box>
                  <Box sx={{ width: "100%", ml: 2 }}>
                    <TextField
                      fullWidth
                      label={t("form.longitudeLabel")}
                      placeholder={t("form.coordinatePlaceholder")}
                      type="text"
                      value={longitude}
                      onChange={(e) => handleCoordinateChange("longitude", e)}
                      size="small"
                      disabled={loading}
                    />
                  </Box>
                </Box>
              )}
            </Box>

            {userRole === Role.Eins && (
              <StyledBox>
                <InputLabel>{t("form.organizationLabel")}</InputLabel>

                <TextField
                  size="small"
                  select
                  margin="dense"
                  fullWidth
                  required
                  SelectProps={{
                    native: true,
                  }}
                  value={selectOrganization}
                  onChange={(e) => {
                    setSelectOrganization(e.target.value);
                  }}
                  disabled={loading}
                >
                  <option value="">{t("form.organizationSelect")}</option>
                  {organizations?.map((organization) => (
                    <option key={organization.id} value={organization.id}>
                      {organization.name}
                    </option>
                  ))}
                </TextField>
              </StyledBox>
            )}

            {stellarFlag && (
              <>
                <Box sx={{ mt: 1 }}>
                  <Box>
                    <InputLabel>{t("form.hivestackScreenIDLabel")}</InputLabel>

                    <TextField
                      size="small"
                      autoFocus
                      margin="dense"
                      fullWidth
                      name="hivestackScreenID"
                      placeholder={t("form.hivestackScreenIDPlaceholder")}
                      value={hivestackID}
                      onChange={(e) => {
                        setHivestackID(e.target.value);
                      }}
                      disabled={loading}
                    />
                  </Box>
                </Box>
                <Box
                  width={"100%"}
                  display={"flex"}
                  justifyContent={"space-between"}
                  sx={{ mt: 1 }}
                >
                  <Box width={"45%"}>
                    <InputLabel>{t("form.vistarDeviceIDLabel")}</InputLabel>

                    <TextField
                      size="small"
                      autoFocus
                      margin="dense"
                      fullWidth
                      name="vistarDeviceID"
                      placeholder={t("form.vistarDeviceIDPlaceholder")}
                      value={vistarDeviceID}
                      onChange={(e) => {
                        setVistarDeviceID(e.target.value);
                      }}
                      disabled={loading}
                    />
                  </Box>

                  <Box width={"45%"}>
                    <InputLabel>{t("form.vistarVenueIDLabel")}</InputLabel>

                    <TextField
                      size="small"
                      autoFocus
                      margin="dense"
                      fullWidth
                      name="vistarVenueID"
                      placeholder={t("form.vistarVenueIDPlaceholder")}
                      value={vistarVenueID}
                      onChange={(e) => {
                        setVistarVenueID(e.target.value);
                      }}
                      disabled={loading}
                    />
                  </Box>
                </Box>
              </>
            )}
          </DialogContent>

          <DialogActions>
            <Button fullWidth variant="outlined" onClick={handleClickClose}>
              {tCommon("cancelBtn")}
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={submitForm}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : tCommon("submitBtn")}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
};

export default CreateViewer;
